export type WorkspaceSearchTermNegateMatchType = 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE';

export type WorkspaceSearchTermNegateJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type WorkspaceSearchTermNegateResultStatus = 'pending' | 'applied' | 'skipped' | 'failed';

export type WorkspaceSearchTermNegateJob = {
  id: string;
  profileId: string;
  status: WorkspaceSearchTermNegateJobStatus;
  matchType: WorkspaceSearchTermNegateMatchType;
  selection: Record<string, unknown>;
  totals: {
    total?: number;
    applied?: number;
    failed?: number;
    skipped?: number;
    pending?: number;
  };
  applyProgress: {
    processed?: number;
    total?: number;
    error?: string;
  };
  startedAt: string | null;
  finishedAt: string | null;
  createdByUserId: string | null;
  createdAt: string | null;
};

export type WorkspaceSearchTermNegateResult = {
  id: string;
  jobId: string;
  searchTermKey: string;
  campaignId: string | null;
  adGroupId: string | null;
  searchTerm: string | null;
  status: WorkspaceSearchTermNegateResultStatus;
  errorCode: string | null;
  errorMessage: string | null;
  appliedAt: string | null;
  retryCount: number;
};

export type WorkspaceSearchTermNegateJobCreateBody = {
  matchType: WorkspaceSearchTermNegateMatchType;
  searchTermKeys?: string[];
  selectAllMatching?: boolean;
  startDate?: string;
  endDate?: string;
  appliedFilter?: Record<string, unknown>;
  columnFilters?: {
    search?: string;
    matchType?: string;
    negated?: string;
  };
  campaignIds?: string[];
};

export const WORKSPACE_SEARCH_TERM_NEGATE_TERMINAL_STATUSES =
  new Set<WorkspaceSearchTermNegateJobStatus>([
    'completed',
    'completed_with_errors',
    'failed',
  ]);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function parseWorkspaceSearchTermNegateJob(
  data: unknown
): WorkspaceSearchTermNegateJob | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const profileId = asString(raw.profileId) ?? asString(raw.profile_id);
  const status = asString(raw.status) as WorkspaceSearchTermNegateJobStatus | null;
  if (!id || !profileId || !status) return null;
  const totals = asRecord(raw.totals);
  const progress = asRecord(raw.applyProgress ?? raw.apply_progress);
  return {
    id,
    profileId,
    status,
    matchType:
      (asString(raw.matchType) as WorkspaceSearchTermNegateMatchType) ||
      (asString(raw.match_type) as WorkspaceSearchTermNegateMatchType) ||
      'NEGATIVE_EXACT',
    selection: asRecord(raw.selection),
    totals: {
      total: asNumber(totals.total) ?? undefined,
      applied: asNumber(totals.applied) ?? undefined,
      failed: asNumber(totals.failed) ?? undefined,
      skipped: asNumber(totals.skipped) ?? undefined,
      pending: asNumber(totals.pending) ?? undefined,
    },
    applyProgress: {
      processed: asNumber(progress.processed) ?? undefined,
      total: asNumber(progress.total) ?? undefined,
      error: asString(progress.error) ?? undefined,
    },
    startedAt: asString(raw.startedAt) ?? asString(raw.started_at),
    finishedAt: asString(raw.finishedAt) ?? asString(raw.finished_at),
    createdByUserId: asString(raw.createdByUserId) ?? asString(raw.created_by_user_id),
    createdAt: asString(raw.createdAt) ?? asString(raw.created_at),
  };
}

export function parseWorkspaceSearchTermNegateResult(
  data: unknown
): WorkspaceSearchTermNegateResult | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const searchTermKey =
    asString(raw.searchTermKey) ?? asString(raw.search_term_key) ?? '';
  const status = asString(raw.status) as WorkspaceSearchTermNegateResultStatus | null;
  if (!id || !status) return null;
  return {
    id,
    jobId: asString(raw.jobId) ?? asString(raw.job_id) ?? '',
    searchTermKey,
    campaignId: asString(raw.campaignId) ?? asString(raw.campaign_id),
    adGroupId: asString(raw.adGroupId) ?? asString(raw.ad_group_id),
    searchTerm: asString(raw.searchTerm) ?? asString(raw.search_term),
    status,
    errorCode: asString(raw.errorCode) ?? asString(raw.error_code),
    errorMessage: asString(raw.errorMessage) ?? asString(raw.error_message),
    appliedAt: asString(raw.appliedAt) ?? asString(raw.applied_at),
    retryCount: asNumber(raw.retryCount) ?? asNumber(raw.retry_count) ?? 0,
  };
}
